import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, addDoc, onSnapshot,
  doc, updateDoc, arrayUnion, serverTimestamp, query, orderBy,
  runTransaction, setDoc, limit as firestoreLimit
} from 'firebase/firestore';
import { signOut, onAuthStateChanged, updateProfile, signInAnonymously } from 'firebase/auth';
import { db, auth, firebaseConfigError } from './firebase';
import {
  PRIMARY_TOPIC_KEYS,
  TOPBAR_TOPIC_KEYS,
  TOPIC_OPTIONS
} from './constants/hixrs';
import {
  calculateLockInCounters,
  getCustomTopicOptions,
  getPostLeader,
  getPostTypeOption,
  getTopicOptionByKey,
  getUserSettings,
  normalizeSide,
  postMatchesTopic,
  userOwnsPost,
  userOwnsThreadItem
} from './utils/hixrs';
import {
  buildNotifications,
  deleteThreadItem,
  getTotalComments,
  getThreadItem,
  makeThreadMessage,
  updateThreadItem
} from './utils/threads';
import {
  isCloudinaryImageUrl,
  uploadImageToCloudinary
} from './services/imageUpload';
import {
  EmptyState,
  SkeletonCard
} from './components/common';
import { HotScreen, PostCard, RightPanel } from './components/feed';
import { PostModal } from './components/PostModal';
import {
  ConfirmModal,
  LoginSheet,
  NotificationCenter,
  ReportModal,
  Toast
} from './components/feedback';
import { CreateScreen, EditHixModal } from './screens/CreateScreen';
import { ProfileScreen, SettingsScreen } from './screens/ProfileScreen';
import { PublicProfileScreen } from './screens/PublicProfileScreen';
import { SearchScreen } from './screens/SearchScreen';
import LandingPage from './LandingPage';
import './App.css';

function MobileNavIcon({ id }) {
  const icons = {
    feed: (
      <>
        <path d="M3.5 10.5 12 3.7l8.5 6.8" />
        <path d="M5.8 9.6v9.2h4.4v-5.1h3.6v5.1h4.4V9.6" />
      </>
    ),
    hot: (
      <>
        <path d="M12.3 21c3.5 0 6.1-2.4 6.1-5.7 0-2.8-1.7-4.6-3.5-6.4-.9-.9-1.7-1.9-1.9-3.3-2.1 1.2-3.3 3-3.1 5.2-1-.4-1.7-1.2-2-2.4-1.4 1.4-2.3 3.5-2.3 5.7C5.6 18.2 8.3 21 12.3 21Z" />
        <path d="M12.1 18.5c1.4 0 2.5-1 2.5-2.4 0-1.1-.7-1.9-1.3-2.5-.4-.4-.8-.9-.9-1.5-1 .7-1.5 1.5-1.4 2.6-.6-.2-1-.6-1.2-1.1-.6.7-1 1.6-1 2.5 0 1.5 1.3 2.4 3.3 2.4Z" />
      </>
    ),
    create: (
      <>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </>
    ),
    search: (
      <>
        <circle cx="12" cy="12" r="8.2" />
        <path d="m14.7 9.3-1.6 4.1-4.1 1.6 1.6-4.1 4.1-1.6Z" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8.2" r="3.2" />
        <path d="M5.6 20c.7-3.2 3-5 6.4-5s5.7 1.8 6.4 5" />
      </>
    )
  };

  if (!icons[id]) return null;

  return (
    <span className={`bnav-icon bnav-icon-${id}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {icons[id]}
      </svg>
    </span>
  );
}

function DesktopNavIcon({ id }) {
  const icons = {
    feed: (
      <>
        <path d="M4 10.6 12 4l8 6.6" />
        <path d="M6.4 9.5v9.2h4v-5h3.2v5h4V9.5" />
      </>
    ),
    hot: (
      <>
        <path d="M12.2 20.5c3.3 0 5.7-2.3 5.7-5.4 0-2.5-1.5-4.1-3.1-5.8-.8-.8-1.5-1.7-1.7-3-1.9 1.1-3 2.8-2.9 4.8-.9-.4-1.6-1.1-1.9-2.2-1.3 1.3-2.1 3.2-2.1 5.3 0 3.8 2.5 6.3 6 6.3Z" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="6.4" />
        <path d="m16 16 4 4" />
      </>
    ),
    thoughts: (
      <>
        <path d="M5 6.5h14v9H9l-4 3v-12Z" />
      </>
    ),
    activity: (
      <>
        <path d="M18 9a6 6 0 1 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
        <path d="M9.8 19a2.3 2.3 0 0 0 4.4 0" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.7 20c.7-3.1 3-4.8 6.3-4.8s5.6 1.7 6.3 4.8" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.8 1.8 0 0 0 .36 2l.04.04a2.1 2.1 0 0 1-2.96 2.96l-.04-.04a1.8 1.8 0 0 0-2-.36 1.8 1.8 0 0 0-1.1 1.65V21a2.1 2.1 0 0 1-4.2 0v-.05A1.8 1.8 0 0 0 8.4 19.3a1.8 1.8 0 0 0-2 .36l-.04.04A2.1 2.1 0 0 1 3.4 16.74l.04-.04a1.8 1.8 0 0 0 .36-2A1.8 1.8 0 0 0 2.15 13H2a2.1 2.1 0 0 1 0-4.2h.15A1.8 1.8 0 0 0 3.8 7.7a1.8 1.8 0 0 0-.36-2l-.04-.04A2.1 2.1 0 0 1 6.36 2.7l.04.04a1.8 1.8 0 0 0 2 .36A1.8 1.8 0 0 0 9.5 1.45V1.4a2.1 2.1 0 0 1 4.2 0v.05a1.8 1.8 0 0 0 1.1 1.65 1.8 1.8 0 0 0 2-.36l.04-.04A2.1 2.1 0 0 1 19.8 5.66l-.04.04a1.8 1.8 0 0 0-.36 2A1.8 1.8 0 0 0 21.05 8.8H21.2a2.1 2.1 0 0 1 0 4.2h-.15A1.8 1.8 0 0 0 19.4 15Z" />
      </>
    )
  };

  if (!icons[id]) return null;

  return (
    <span className={`desktop-nav-icon desktop-nav-icon-${id}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {icons[id]}
      </svg>
    </span>
  );
}

function FirebaseConfigMissing() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, background: '#08080F', color: '#F8FAFC', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 560, padding: 24, border: '1px solid #273044', borderRadius: 14, background: '#111827' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: 1 }}>Setup needed</div>
        <h1 style={{ margin: '10px 0 8px', fontSize: 24 }}>Firebase config is missing</h1>
        <p style={{ margin: '0 0 14px', color: '#CBD5E1', lineHeight: 1.5 }}>
          Add your Firebase values to a local .env file, then restart the app.
        </p>
        <a href="/" style={{ minHeight: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '0 0 14px', padding: '0 14px', borderRadius: 8, background: '#F8FAFC', color: '#111827', fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
          Back to landing page
        </a>
        <pre style={{ margin: 0, padding: 14, overflowX: 'auto', borderRadius: 10, background: '#020617', color: '#E2E8F0', fontSize: 12 }}>{firebaseConfigError}</pre>
      </div>
    </div>
  );
}

const REPORT_REASONS = ['Spam', 'Harassment', 'Hate', 'Violence', 'Misinformation', 'Nudity', 'Other'];

const SAVE_IDENTITY_PROMPT = {
  title: 'Save your opinion identity?',
  subtitle: 'Your lock-in is saved on this device. Sign in with Google to keep your Hix history across devices, comment, and build your profile.',
  cta: 'Save with Google'
};

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function safeReportIdPart(value, fallback = 'unknown') {
  const cleaned = String(value || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 120);
  return cleaned || fallback;
}

function getReportTargetKey(target) {
  if (!target?.postId) return '';
  const postId = safeReportIdPart(target.postId, '');
  if (!postId) return '';

  if (target.type === 'comment') {
    const path = Array.isArray(target.commentPath)
      ? target.commentPath
        .map(part => Number.parseInt(part, 10))
        .filter(part => Number.isInteger(part) && part >= 0)
      : [];

    if (path.length === 0) return '';
    return `comment_${postId}_${path.join('-')}`;
  }

  return `post_${postId}`;
}

function getReportDocId(uid, targetKey) {
  return `${uid}_${targetKey}`;
}

function cleanReportPreview(value) {
  return String(value || 'Reported content').replace(/\s+/g, ' ').trim().slice(0, 180) || 'Reported content';
}

function requireCloudinaryImage(imageUrl) {
  if (!imageUrl) return null;
  if (!isCloudinaryImageUrl(imageUrl)) {
    throw new Error('Please upload an image instead of pasting an external image link.');
  }
  return imageUrl;
}

function HixrsApp() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [screen, setScreen] = useState('feed');
  const [feedTab, setFeedTab] = useState('all');
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [votes, setVotes] = useState({});
  const [flippedVotes, setFlippedVotes] = useState({});
  const [pendingLockIns, setPendingLockIns] = useState({});
  const [openPost, setOpenPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [toast, setToast] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [authPrompt, setAuthPrompt] = useState(null);
  const [showFeedMore, setShowFeedMore] = useState(false);
  const [showTopbarMore, setShowTopbarMore] = useState(false);
  const [showSidebarMore, setShowSidebarMore] = useState(false);
  const [dataErrors, setDataErrors] = useState({});
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const modalHistoryRef = useRef(false);
  const guestSavePromptShownRef = useRef(false);

  const setDataError = useCallback((key, message = '') => {
    setDataErrors(prev => ({ ...prev, [key]: message }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = () => setSystemTheme(media.matches ? 'dark' : 'light');

    handleThemeChange();
    media.addEventListener?.('change', handleThemeChange);
    return () => media.removeEventListener?.('change', handleThemeChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName;
      if (e.key === '/' && activeTag !== 'INPUT' && activeTag !== 'TEXTAREA') {
        e.preventDefault();
        setScreen('search');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const requestConfirm = (dialog) => {
    setConfirmDialog(dialog);
  };

  const openLoginSheet = (prompt = null) => {
    setAuthPrompt(prompt || (user?.isAnonymous ? SAVE_IDENTITY_PROMPT : null));
    setShowLogin(true);
  };

  const requireLogin = (action, prompt = null) => {
    if (user && !user.isAnonymous) {
      action();
    } else {
      setPendingAction(() => action);
      openLoginSheet(prompt);
    }
  };

  const openPostWithHistory = useCallback((post) => {
    if (!post) return;
    setOpenPost(post);
    if (!window.history.state?.hixrsPostModal) {
      window.history.pushState({
        ...(window.history.state || {}),
        hixrsPostModal: true,
        postId: post.id
      }, '', window.location.href);
    }
    modalHistoryRef.current = true;
  }, []);

  const closeOpenPost = useCallback(() => {
    setOpenPost(null);
    if (modalHistoryRef.current && window.history.state?.hixrsPostModal) {
      modalHistoryRef.current = false;
      window.history.back();
      return;
    }
    modalHistoryRef.current = false;
  }, []);

  useEffect(() => {
    const handlePostModalBack = () => {
      if (!modalHistoryRef.current) return;
      modalHistoryRef.current = false;
      setOpenPost(null);
    };

    window.addEventListener('popstate', handlePostModalBack);
    return () => window.removeEventListener('popstate', handlePostModalBack);
  }, []);

  const goHome = () => {
    setScreen('feed');
    setFeedTab('all');
    closeOpenPost();
    setShowFeedMore(false);
    setShowTopbarMore(false);
    setShowSidebarMore(false);
    setSelectedProfile(null);
  };

  const selectTopic = (topicKey) => {
    setFeedTab(topicKey);
    setScreen('feed');
    setShowFeedMore(false);
    setShowTopbarMore(false);
    setShowSidebarMore(false);
    setSelectedProfile(null);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthLoading(false);
      if (u) {
        setDoc(doc(db, 'users', u.uid), {
          uid: u.uid,
          displayName: u.isAnonymous ? 'Guest Hixr' : u.displayName || 'Anonymous',
          email: u.email || '',
          photoURL: u.isAnonymous ? '' : u.photoURL || '',
          lastSeenAt: serverTimestamp()
        }, { merge: true }).catch(console.error);
      }
      if (u && !u.isAnonymous && pendingAction) {
        pendingAction();
        setPendingAction(null);
        setShowLogin(false);
        setAuthPrompt(null);
      }
    }, error => {
      console.error(error);
      setAuthLoading(false);
      setDataError('auth', 'Could not connect to authentication. Refresh and try again.');
    });
    return unsub;
  }, [pendingAction, setDataError]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setDataError('profile', '');
      return;
    }

    const unsub = onSnapshot(doc(db, 'users', user.uid), snapshot => {
      setDataError('profile', '');
      setProfile(snapshot.exists() ? snapshot.data() : null);
    }, error => {
      console.error(error);
      setDataError('profile', 'Could not load your profile settings.');
    });
    return unsub;
  }, [user, setDataError]);
  useEffect(() => {
    if (!user) {
      setVotes({});
      setFlippedVotes({});
      setDataError('votes', '');
      return;
    }
    const unsub = onSnapshot(collection(db, 'users', user.uid, 'votes'), snapshot => {
      setDataError('votes', '');
      const savedVotes = {};
      const savedFlipped = {};
      snapshot.docs.forEach(d => {
        const data = d.data();
        savedVotes[d.id] = data.side;
        savedFlipped[d.id] = Boolean(data.flipped);
      });
      setVotes(savedVotes);
      setFlippedVotes(savedFlipped);
    }, error => {
      console.error(error);
      setDataError('votes', 'Could not load your saved lock-ins.');
    });
    return unsub;
  }, [user, setDataError]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), snapshot => {
      setDataError('users', '');
      setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, error => {
      console.error(error);
      setDataError('users', 'Some profile data could not load.');
    });
    return unsub;
  }, [setDataError]);
  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'), firestoreLimit(80));
    const unsub = onSnapshot(q, snapshot => {
      setDataError('posts', '');
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPosts(data);
      setPostsLoading(false);
    }, error => {
      console.error(error);
      setPostsLoading(false);
      setDataError('posts', 'Could not load Hixs. Check your connection and refresh.');
    });
    return unsub;
  }, [setDataError]);

  const handleProfileOpen = (target) => {
    if (!target) return;
    setSelectedProfile(target);
    closeOpenPost();
    setScreen('publicProfile');
    setShowFeedMore(false);
    setShowTopbarMore(false);
    setShowSidebarMore(false);
  };

  const handleFollowToggle = async (targetUid) => {
    if (!user || !targetUid || targetUid === user.uid) return;

    try {
      const result = await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await transaction.get(userRef);
        const current = userSnap.exists() ? userSnap.data() : {};
        const following = Array.isArray(current.following) ? current.following : [];
        const isFollowing = following.includes(targetUid);
        const nextFollowing = isFollowing
          ? following.filter(uid => uid !== targetUid)
          : [...following, targetUid];

        transaction.set(userRef, {
          uid: user.uid,
          displayName: profile?.displayName || user.displayName || 'Anonymous',
          email: user.email || '',
          photoURL: profile?.photoURL || user.photoURL || '',
          following: nextFollowing,
          updatedAt: serverTimestamp()
        }, { merge: true });

        return { following: !isFollowing };
      });

      showToast(result.following ? 'Following.' : 'Unfollowed.');
    } catch (e) {
      console.error(e);
      showToast('Could not update follow.');
    }
  };
  const handleVote = async (postId, side) => {
    const cleanSide = normalizeSide(side);
    if (!cleanSide) return;

    let actor = user || auth.currentUser;
    if (!actor) {
      try {
        const credential = await signInAnonymously(auth);
        actor = credential.user;
      } catch (e) {
        console.error(e);
        setAuthPrompt({
          title: 'Lock in faster',
          subtitle: 'Guest lock-in is not enabled yet. Sign in with Google to lock in and build your opinion identity.',
          cta: 'Continue with Google'
        });
        setShowLogin(true);
        return;
      }
    }

    if (votes[postId]) {
      showToast('You already locked in on this Hix.');
      return;
    }
    if (pendingLockIns[postId]) return;

    const actorUid = actor.uid;
    const actorIsGuest = Boolean(actor.isAnonymous);
    const actorName = actorIsGuest ? 'Guest Hixr' : profile?.displayName || actor.displayName || 'Anonymous';
    const actorPhoto = actorIsGuest ? '' : profile?.photoURL || actor.photoURL || '';

    setPendingLockIns(prev => ({ ...prev, [postId]: cleanSide }));
    setVotes(prev => ({ ...prev, [postId]: cleanSide }));

    try {
      const result = await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const lockInRef = doc(db, 'posts', postId, 'lockIns', actorUid);
        const voteRef = doc(db, 'users', actorUid, 'votes', postId);
        const postSnap = await transaction.get(postRef);
        const lockInSnap = await transaction.get(lockInRef);
        const voteSnap = await transaction.get(voteRef);

        if (!postSnap.exists()) throw new Error('This Hix no longer exists.');

        const post = postSnap.data();
        const existingLockIn = lockInSnap.exists() ? lockInSnap.data() : null;
        const existingVote = voteSnap.exists() ? voteSnap.data() : null;
        const existingSide = normalizeSide(existingLockIn?.side || existingVote?.side);

        if (existingSide) {
          const sideLabel = existingSide === 'a' ? post.sideA : post.sideB;
          const existingPayload = {
            postId,
            userId: actorUid,
            side: existingSide,
            sideLabel,
            question: post.question || existingVote?.question || '',
            topic: post.topic || existingVote?.topic || '',
            postType: post.postType || existingVote?.postType || 'take',
            authorId: post.authorId || '',
            flipped: Boolean(existingLockIn?.flipped || existingVote?.flipped),
            lockInVersion: 2,
            updatedAt: serverTimestamp()
          };

          if (!lockInSnap.exists()) {
            transaction.set(lockInRef, {
              ...existingPayload,
              userName: actorName,
              userPhoto: actorPhoto,
              lockedAt: existingVote?.createdAt || serverTimestamp(),
              createdAt: existingVote?.createdAt || serverTimestamp()
            }, { merge: true });
          }

          if (!voteSnap.exists()) {
            transaction.set(voteRef, {
              ...existingPayload,
              lockInPath: `posts/${postId}/lockIns/${actorUid}`,
              createdAt: existingLockIn?.createdAt || existingLockIn?.lockedAt || serverTimestamp()
            }, { merge: true });
          }

          return {
            duplicate: true,
            side: existingSide,
            flipped: Boolean(existingLockIn?.flipped || existingVote?.flipped)
          };
        }

        const previousLeader = post.currentLeader || getPostLeader(post);
        const nextVotesA = Number(post.votesA || 0) + (cleanSide === 'a' ? 1 : 0);
        const nextVotesB = Number(post.votesB || 0) + (cleanSide === 'b' ? 1 : 0);
        const counters = calculateLockInCounters(nextVotesA, nextVotesB);
        const flipped = previousLeader !== 'tie' && counters.currentLeader !== 'tie' && previousLeader !== counters.currentLeader;
        const sideLabel = cleanSide === 'a' ? post.sideA : post.sideB;
        const baseLockIn = {
          postId,
          userId: actorUid,
          userName: actorName,
          userPhoto: actorPhoto,
          side: cleanSide,
          sideLabel,
          question: post.question || '',
          topic: post.topic || '',
          postType: post.postType || 'take',
          authorId: post.authorId || '',
          flipped: false,
          lockInVersion: 2,
          lockedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        transaction.set(lockInRef, baseLockIn);
        transaction.set(voteRef, {
          ...baseLockIn,
          lockInPath: `posts/${postId}/lockIns/${actorUid}`
        });

        transaction.update(postRef, {
          ...counters,
          lockInModelVersion: 2,
          flipCount: (post.flipCount || 0) + (flipped ? 1 : 0),
          updatedAt: serverTimestamp()
        });

        return {
          side: cleanSide,
          pct: cleanSide === 'a' ? counters.pctA : counters.pctB,
          isMaj: cleanSide === 'a' ? counters.pctA >= counters.pctB : counters.pctB >= counters.pctA,
          flipped
        };
      });

      setVotes(prev => ({ ...prev, [postId]: result.side || cleanSide }));
      setFlippedVotes(prev => ({ ...prev, [postId]: Boolean(result.flipped) }));
      if (result.duplicate) {
        showToast('You already locked in on this Hix.');
        return;
      }
      const resultMessage = result.flipped
        ? 'This Hix flipped - the minority moved the room!'
        : result.isMaj
          ? `You are in the ${result.pct}% majority.`
          : `You are in the ${result.pct}% minority. Defend it.`;

      showToast(resultMessage);

      if (actorIsGuest && !guestSavePromptShownRef.current) {
        guestSavePromptShownRef.current = true;
        window.setTimeout(() => {
          setAuthPrompt(SAVE_IDENTITY_PROMPT);
          setShowLogin(true);
        }, 650);
      }
    } catch (e) {
      console.error(e);
      setVotes(prev => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      showToast(e.message || 'Could not save your lock-in.');
    } finally {
      setPendingLockIns(prev => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleFlip = async (postId, nextSide) => {
    const cleanNextSide = normalizeSide(nextSide);
    if (!user || !cleanNextSide) return;

    try {
      const result = await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const lockInRef = doc(db, 'posts', postId, 'lockIns', user.uid);
        const voteRef = doc(db, 'users', user.uid, 'votes', postId);
        const postSnap = await transaction.get(postRef);
        const lockInSnap = await transaction.get(lockInRef);
        const voteSnap = await transaction.get(voteRef);

        if (!postSnap.exists()) throw new Error('This Hix no longer exists.');

        const post = postSnap.data();
        const lockIn = lockInSnap.exists() ? lockInSnap.data() : null;
        const vote = voteSnap.exists() ? voteSnap.data() : null;
        const previousSide = normalizeSide(lockIn?.side || vote?.side);

        if (!previousSide) throw new Error('Lock in before flipping.');
        if (lockIn?.flipped || vote?.flipped) throw new Error('You already used your flip on this Hix.');
        if (previousSide === cleanNextSide) throw new Error('Could not flip this lock-in.');

        let votesA = Number(post.votesA || 0);
        let votesB = Number(post.votesB || 0);

        if (previousSide === 'a') {
          votesA = Math.max(0, votesA - 1);
          votesB += 1;
        } else {
          votesB = Math.max(0, votesB - 1);
          votesA += 1;
        }

        const counters = calculateLockInCounters(votesA, votesB);
        const sideLabel = cleanNextSide === 'a' ? post.sideA : post.sideB;
        const userName = profile?.displayName || user.displayName || 'Anonymous';
        const userPhoto = profile?.photoURL || user.photoURL || '';
        const lockInUpdate = {
          postId,
          userId: user.uid,
          userName,
          userPhoto,
          side: cleanNextSide,
          sideLabel,
          previousSide,
          question: post.question || vote?.question || '',
          topic: post.topic || vote?.topic || '',
          postType: post.postType || vote?.postType || 'take',
          authorId: post.authorId || vote?.authorId || '',
          flipped: true,
          flippedAt: serverTimestamp(),
          lockInVersion: 2,
          updatedAt: serverTimestamp()
        };

        transaction.set(lockInRef, {
          ...lockInUpdate,
          lockedAt: lockIn?.lockedAt || vote?.createdAt || serverTimestamp(),
          createdAt: lockIn?.createdAt || vote?.createdAt || serverTimestamp()
        }, { merge: true });

        transaction.set(voteRef, {
          ...lockInUpdate,
          lockInPath: `posts/${postId}/lockIns/${user.uid}`,
          createdAt: vote?.createdAt || lockIn?.createdAt || lockIn?.lockedAt || serverTimestamp()
        }, { merge: true });

        transaction.update(postRef, {
          ...counters,
          lockInModelVersion: 2,
          flipCount: (post.flipCount || 0) + 1,
          updatedAt: serverTimestamp()
        });

        return {
          side: cleanNextSide,
          sideLabel,
          pct: cleanNextSide === 'a' ? counters.pctA : counters.pctB,
          isMaj: cleanNextSide === 'a' ? counters.pctA >= counters.pctB : counters.pctB >= counters.pctA
        };
      });

      setVotes(prev => ({ ...prev, [postId]: result.side }));
      setFlippedVotes(prev => ({ ...prev, [postId]: true }));
      showToast(result.isMaj ? `Flipped to ${result.sideLabel}. You are now in the ${result.pct}% majority.` : `Flipped to ${result.sideLabel}. You are now in the ${result.pct}% minority.`);
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Could not flip this Hix.');
    }
  };
  const handleHeat = async (postId) => {
    if (!user) return;

    try {
      const result = await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await transaction.get(postRef);

        if (!postSnap.exists()) {
          throw new Error('This Hix no longer exists.');
        }

        const postData = postSnap.data();
        const heatedBy = Array.isArray(postData.heatedBy) ? postData.heatedBy : [];
        const hasHeated = heatedBy.includes(user.uid);
        const nextHeatedBy = hasHeated
          ? heatedBy.filter(uid => uid !== user.uid)
          : [...heatedBy, user.uid];
        const baseHeatCount = Number.isFinite(Number(postData.heatCount))
          ? Number(postData.heatCount)
          : heatedBy.length;

        transaction.update(postRef, {
          heatCount: Math.max(0, baseHeatCount + (hasHeated ? -1 : 1)),
          heatedBy: nextHeatedBy,
          updatedAt: serverTimestamp()
        });

        return { heated: !hasHeated };
      });
      showToast(result.heated ? 'Heat added.' : 'Heat removed.');
    } catch (e) {
      console.error(e);
      showToast('Could not update heat.');
    }
  };

  const handleComment = async (postId, text, side, image) => {
    if (!user) return;
    const imageUrl = requireCloudinaryImage(await uploadImageToCloudinary(image, { userId: user.uid }));
    const comment = makeThreadMessage({ user, profile, side, text, image: imageUrl });
    await updateDoc(doc(db, 'posts', postId), { comments: arrayUnion(comment), updatedAt: serverTimestamp() });
    showToast('Comment posted.');
  };

  const handleReply = async (postId, path, text, side, image) => {
    if (!user) return;
    const imageUrl = requireCloudinaryImage(await uploadImageToCloudinary(image, { userId: user.uid }));
    const reply = makeThreadMessage({ user, profile, side, text, image: imageUrl });
    try {
      await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error('This Hix no longer exists.');
        const postData = postSnap.data();
        const comments = updateThreadItem(postData.comments || [], path, target => ({
          ...target,
          replies: [...(target.replies || []), reply]
        }));
        transaction.update(postRef, { comments, updatedAt: serverTimestamp() });
      });
      showToast('Reply posted.');
    } catch (e) {
      console.error(e);
      showToast('Could not post reply.');
    }
  };

  const handleCommentLike = async (postId, path) => {
    if (!user) return;
    try {
      const result = await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error('This Hix no longer exists.');
        const postData = postSnap.data();
        let action = null;
        const comments = updateThreadItem(postData.comments || [], path, target => {
          const likedBy = Array.isArray(target.likedBy) ? target.likedBy : [];
          const hasLiked = likedBy.includes(user.uid);
          const baseLikes = Number.isFinite(Number(target.likes)) ? Number(target.likes) : likedBy.length;
          action = hasLiked ? 'unliked' : 'liked';
          return {
            ...target,
            likedBy: hasLiked ? likedBy.filter(uid => uid !== user.uid) : [...likedBy, user.uid],
            likes: Math.max(0, baseLikes + (hasLiked ? -1 : 1))
          };
        });
        if (!action) throw new Error('Comment no longer exists.');
        transaction.update(postRef, { comments, updatedAt: serverTimestamp() });
        return { liked: action === 'liked' };
      });
      showToast(result.liked ? 'Comment liked.' : 'Comment unliked.');
    } catch (e) {
      console.error(e);
      showToast('Could not update this comment.');
    }
  };

  const handleCommentDelete = async (postId, path) => {
    if (!user) return;
    try {
      await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) throw new Error('This Hix no longer exists.');

        const postData = postSnap.data();
        const target = getThreadItem(postData.comments || [], path);
        if (!target) throw new Error('This comment no longer exists.');
        if (target.deleted) return;
        if (!userOwnsThreadItem(target, user, profile)) throw new Error('You can only delete your own comment.');

        const comments = deleteThreadItem(postData.comments || [], path);
        transaction.update(postRef, { comments, updatedAt: serverTimestamp() });
      });
      showToast('Comment deleted.');
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Could not delete this comment.');
    }
  };
  const handleCreatePost = async ({ question, sideA, sideB, topic, postType, image }) => {
    if (!user) return;
    const imageUrl = requireCloudinaryImage(await uploadImageToCloudinary(image, { userId: user.uid }));
    const cleanPostType = getPostTypeOption(postType).key;
    const isThoughtPost = cleanPostType === 'thought';
    await addDoc(collection(db, 'posts'), {
      question, sideA: isThoughtPost ? '' : sideA, sideB: isThoughtPost ? '' : sideB, topic,
      postType: cleanPostType,
      image: imageUrl || null,
      pctA: 50, pctB: 50, votesA: 0, votesB: 0,
      totalVotes: 0, heat: 50, comments: [],
      heatCount: 0, heatedBy: [],
      currentLeader: 'tie', flipCount: 0, lockInModelVersion: 2,
      authorId: user.uid,
      authorName: profile?.displayName || user.displayName || 'Anonymous',
      authorPhoto: profile?.photoURL || user.photoURL || '',
      authorHandle: profile?.username || '',
      createdAt: serverTimestamp()
    });
    setScreen('feed');
    showToast(isThoughtPost ? 'Your Open Thought is live.' : 'Your post is live.');
  };

  const handlePostEdit = async (post, updates) => {
    if (!user || !post) return;
    const imageUrl = requireCloudinaryImage(await uploadImageToCloudinary(updates.image, { userId: user.uid }));

    await runTransaction(db, async (transaction) => {
      const postRef = doc(db, 'posts', post.id);
      const postSnap = await transaction.get(postRef);
      if (!postSnap.exists()) throw new Error('This Hix no longer exists.');

      const postData = { id: post.id, ...postSnap.data() };
      if (!userOwnsPost(postData, user, profile)) {
        throw new Error('You can only edit your own Hix.');
      }

      const hasLockIns = Number(postData.totalVotes || 0) > 0;
      const cleanTopic = updates.topic.trim().slice(0, 40);
      const cleanPostType = getPostTypeOption(updates.postType).key;
      const isThoughtPost = cleanPostType === 'thought';
      const cleanQuestion = updates.question.trim().slice(0, isThoughtPost ? 280 : 180);
      const cleanSideA = updates.sideA.trim().slice(0, 40);
      const cleanSideB = updates.sideB.trim().slice(0, 40);

      if (cleanQuestion.length < 8) throw new Error('Question needs at least 8 characters.');
      if (!cleanTopic) throw new Error('Pick a topic.');
      if (hasLockIns && cleanPostType !== (postData.postType || 'take')) throw new Error('Post type is locked after the first lock-in.');
      if (!hasLockIns && !isThoughtPost) {
        if (!cleanSideA || !cleanSideB) throw new Error('Add both sides.');
        if (cleanSideA.toLowerCase() === cleanSideB.toLowerCase()) throw new Error('Both sides cannot be the same.');
      } else if (!isThoughtPost && (cleanSideA !== postData.sideA || cleanSideB !== postData.sideB)) {
        throw new Error('Sides are locked after the first lock-in.');
      }

      const editPayload = {
        question: cleanQuestion,
        sideA: isThoughtPost ? '' : hasLockIns ? postData.sideA : cleanSideA,
        sideB: isThoughtPost ? '' : hasLockIns ? postData.sideB : cleanSideB,
        topic: cleanTopic,
        postType: cleanPostType,
        image: imageUrl || null,
        updatedAt: serverTimestamp()
      };

      transaction.update(postRef, editPayload);
    });

    if (openPost?.id === post.id) {
      setOpenPost(prev => prev ? {
        ...prev,
        question: updates.question.trim().slice(0, getPostTypeOption(updates.postType).key === 'thought' ? 280 : 180),
        topic: updates.topic.trim().slice(0, 40),
        postType: getPostTypeOption(updates.postType).key,
        image: imageUrl || null,
        sideA: getPostTypeOption(updates.postType).key === 'thought' ? '' : Number(post.totalVotes || 0) > 0 ? prev.sideA : updates.sideA.trim().slice(0, 40),
        sideB: getPostTypeOption(updates.postType).key === 'thought' ? '' : Number(post.totalVotes || 0) > 0 ? prev.sideB : updates.sideB.trim().slice(0, 40)
      } : prev);
    }

    showToast('Hix updated.');
  };

  const requestPostEdit = (post) => {
    requireLogin(() => {
      if (!userOwnsPost(post, user, profile)) {
        showToast('You can only edit your own Hix.');
        return;
      }
      setEditingPost(post);
    });
  };
  const handlePostDelete = async (post) => {
    if (!user || !post) return;


    try {
      await runTransaction(db, async (transaction) => {
        const postRef = doc(db, 'posts', post.id);
        const postSnap = await transaction.get(postRef);
        if (!postSnap.exists()) return;

        const postData = { id: post.id, ...postSnap.data() };
        if (!userOwnsPost(postData, user, profile)) {
          throw new Error('You can only delete your own Hix.');
        }

        transaction.delete(postRef);
      });

      if (openPost?.id === post.id) closeOpenPost();
      showToast('Hix deleted.');
    } catch (e) {
      console.error(e);
      showToast(e.message || 'Could not delete this Hix.');
    }
  };

  const requestPostDelete = (post) => {
    requireLogin(() => {
      requestConfirm({
        title: 'Delete post?',
        message: "Are you sure you want to delete your post? You can't undo this.",
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'danger',
        onConfirm: () => handlePostDelete(post)
      });
    });
  };
  const handleReportSubmit = async ({ reason, details }) => {
    if (!user || !reportTarget) return;
    const targetKey = getReportTargetKey(reportTarget);
    if (!targetKey) {
      showToast('Could not report this item.');
      return;
    }

    const type = reportTarget.type === 'comment' ? 'comment' : 'post';
    const commentPath = type === 'comment' && Array.isArray(reportTarget.commentPath)
      ? reportTarget.commentPath
        .map(part => Number.parseInt(part, 10))
        .filter(part => Number.isInteger(part) && part >= 0)
      : [];
    const reportDocId = getReportDocId(user.uid, targetKey);
    const safeReason = REPORT_REASONS.includes(reason) ? reason : 'Other';

    try {
      await setDoc(doc(db, 'reports', reportDocId), {
        type,
        postId: reportTarget.postId,
        commentPath,
        targetKey,
        preview: cleanReportPreview(reportTarget.preview),
        reason: safeReason,
        details: String(details || '').trim().slice(0, 220),
        reporterId: user.uid,
        reporterName: profile?.displayName || user.displayName || 'Anonymous',
        status: 'open',
        createdAt: serverTimestamp()
      });
      setReportTarget(null);
      showToast('Report submitted.');
    } catch (e) {
      console.error(e);
      setReportTarget(null);
      showToast('You already reported this item or the report was blocked.');
    }
  };

  const handleSaveProfile = async ({ displayName, username, bio, photoURL, bannerURL }) => {
    if (!user) return;

    const storedPhotoURL = await uploadImageToCloudinary(photoURL, { userId: user.uid });
    const storedBannerURL = await uploadImageToCloudinary(bannerURL || '', { userId: user.uid });
    const finalPhotoURL = storedPhotoURL || '';
    const finalBannerURL = storedBannerURL || '';

    if (auth.currentUser) {
      await updateProfile(auth.currentUser, {
        displayName,
        photoURL: finalPhotoURL || null
      });
      setUser({
        uid: auth.currentUser.uid,
        displayName: auth.currentUser.displayName,
        email: auth.currentUser.email,
        photoURL: auth.currentUser.photoURL
      });
    }

    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      displayName,
      username,
      bio,
      photoURL: finalPhotoURL,
      bannerURL: finalBannerURL,
      email: user.email || '',
      updatedAt: serverTimestamp()
    }, { merge: true });

    showToast('Profile updated.');
  };

  const handleSaveSettings = async (settings) => {
    if (!user) return;
    await setDoc(doc(db, 'users', user.uid), {
      settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
    showToast('Settings saved.');
  };
  const handleSignOut = async () => {
    await signOut(auth);
    setVotes({});
    setScreen('feed');
    showToast('Signed out. See you soon!');
  };

  const handleNotificationRead = async (notificationId) => {
    if (!user || !notificationId) return;

    try {
      await setDoc(doc(db, 'users', user.uid), {
        readNotifications: arrayUnion(notificationId),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    if (!user) return;
    const unreadIds = notifications.filter(item => !item.read).map(item => item.id);
    if (unreadIds.length === 0) return;

    try {
      await setDoc(doc(db, 'users', user.uid), {
        readNotifications: arrayUnion(...unreadIds),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (e) {
      console.error(e);
      showToast('Could not mark notifications read.');
    }
  };
  const activePost = openPost ? posts.find(p => p.id === openPost.id) || openPost : null;
  const accountUser = user && !user.isAnonymous ? user : null;
  const readNotificationIds = Array.isArray(profile?.readNotifications) ? profile.readNotifications : [];
  const viewerSettings = getUserSettings(profile);
  const notificationPrefs = viewerSettings.notifications;
  const themePreference = viewerSettings.preferences.theme || 'system';
  const resolvedTheme = themePreference === 'system' ? systemTheme : themePreference;
  const feedDensity = viewerSettings.preferences.feedDensity === 'compact' ? 'compact' : 'comfortable';
  const defaultPostType = viewerSettings.preferences.defaultPostType || 'quick';
  const notifications = buildNotifications(posts, accountUser?.uid, notificationPrefs)
    .map(item => ({ ...item, read: readNotificationIds.includes(item.id) }));
  const unreadNotificationCount = notifications.filter(item => !item.read).length;
  const viewerName = profile?.displayName || accountUser?.displayName || 'User';
  const viewerPhoto = profile?.photoURL || accountUser?.photoURL || '';
  const viewerInitial = viewerName?.[0]?.toUpperCase() || 'U';

  const customFeedTopics = getCustomTopicOptions(posts);
  const primaryFeedTopics = ['all', 'hixs', 'thoughts', 'hotmix', ...PRIMARY_TOPIC_KEYS];
  const moreFeedTopics = [
    ...TOPIC_OPTIONS.filter(topic => !PRIMARY_TOPIC_KEYS.includes(topic.key)),
    ...customFeedTopics
  ];
  const topbarTopics = TOPBAR_TOPIC_KEYS.map(getTopicOptionByKey).filter(Boolean);
  const topbarMoreTopics = [
    ...TOPIC_OPTIONS.filter(topic => !TOPBAR_TOPIC_KEYS.includes(topic.key)),
    ...customFeedTopics
  ];
  const selectedMoreTopic = moreFeedTopics.find(topic => topic.key === feedTab);
  const topbarMoreActive = showTopbarMore || topbarMoreTopics.some(topic => topic.key === feedTab);
  const sidebarMoreActive = showSidebarMore || Boolean(selectedMoreTopic);
  const topicLabels = {
    all: 'All',
    hixs: 'Hixs',
    thoughts: 'Thoughts',
    hotmix: 'Hot',
    ...TOPIC_OPTIONS.reduce((acc, topic) => ({ ...acc, [topic.key]: topic.label }), {})
  };

  const filteredPosts = posts
    .filter(p => postMatchesTopic(p, feedTab))
    .sort((a, b) => feedTab === 'hotmix'
      ? ((b.heatCount || 0) * 4 + getTotalComments(b.comments || []) * 6 + (b.totalVotes || 0) * 2) -
        ((a.heatCount || 0) * 4 + getTotalComments(a.comments || []) * 6 + (a.totalVotes || 0) * 2)
      : 0);
  const visibleDataError = dataErrors.auth || dataErrors.posts || dataErrors.profile || dataErrors.votes || dataErrors.users;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;

    return () => {
      document.documentElement.removeAttribute('data-theme');
      document.documentElement.style.colorScheme = '';
    };
  }, [resolvedTheme]);

  const navItems = [
    { id: 'feed', label: 'Hixs' },
    { id: 'hot', label: 'Hot' },
    { id: 'create', label: 'Post' },
    { id: 'search', label: 'Explore' },
    { id: 'profile', label: 'You' },
  ];

  const sidebarItems = [
    { id: 'feed', label: 'Hixs', icon: 'feed', action: () => { setFeedTab('all'); setScreen('feed'); }, active: screen === 'feed' && feedTab !== 'thoughts' },
    { id: 'hot', label: 'Hot', icon: 'hot', action: () => setScreen('hot'), active: screen === 'hot' },
    { id: 'search', label: 'Explore', icon: 'search', action: () => setScreen('search'), active: screen === 'search' },
    { id: 'thoughts', label: 'Thoughts', icon: 'thoughts', action: () => { setFeedTab('thoughts'); setScreen('feed'); }, active: screen === 'feed' && feedTab === 'thoughts' },
    { id: 'activity', label: 'Activity', icon: 'activity', action: () => accountUser ? setShowNotifications(prev => !prev) : openLoginSheet(), active: showNotifications },
    { id: 'profile', label: 'Profile', icon: 'profile', action: () => { setSelectedProfile(null); setScreen('profile'); }, active: screen === 'profile' || screen === 'publicProfile' },
    { id: 'settings', label: 'Settings', icon: 'settings', action: () => setScreen('settings'), active: screen === 'settings' }
  ];

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#08080F' }}>
        <div style={{ color: '#52516B', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={`app feed-density-${feedDensity} theme-${resolvedTheme}`}>

      <div className="app-layout">
        <nav className="sidebar">
          <div className="sidebar-logo" onClick={goHome}>hixrs<span>.</span></div>
          <div className="sidebar-nav">
            {sidebarItems.map(item => (
              <div key={item.id}
                className={`nav-item ${item.active ? 'active' : ''}`}
                onClick={item.action}>
                <DesktopNavIcon id={item.icon} />
                <span className="nav-label">{item.label}</span>
              </div>
            ))}
          </div>
          <button type="button" className="sidebar-create-btn" onClick={() => setScreen('create')}>
            <span>+</span>
            Create Hix
          </button>
          <div className="sidebar-divider sidebar-topic-divider"></div>
          <div className="sidebar-section-label">Topics</div>
          {PRIMARY_TOPIC_KEYS.map(getTopicOptionByKey).filter(Boolean).map(t => (
            <div key={t.key} className="sidebar-topic"
              onClick={() => selectTopic(t.key)}>
              {t.label}
            </div>
          ))}
          <button type="button" className={`sidebar-topic sidebar-more-btn ${sidebarMoreActive ? 'active' : ''}`} onClick={() => setShowSidebarMore(prev => !prev)}>
            More
          </button>
          {showSidebarMore && (
            <div className="sidebar-more-topics">
              {moreFeedTopics.map(t => (
                <button key={t.key} type="button" className={`sidebar-topic ${feedTab === t.key ? 'active' : ''}`} onClick={() => selectTopic(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <div className="sidebar-divider"></div>
          {accountUser ? (
            <div className="sidebar-user">
              <div className="su-av">
                {viewerPhoto ? <img src={viewerPhoto} alt="av" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : viewerInitial}
              </div>
              <div className="su-name">{viewerName.split(' ')[0]}</div>
              <button className="su-out" onClick={handleSignOut} title="Sign out">↩</button>
            </div>
          ) : (
            <button className="sidebar-login-btn" onClick={() => openLoginSheet()}>
              Sign in
            </button>
          )}
        </nav>

        <main className="main-content">
          <div className={`topbar topbar-screen-${screen}`}>
            <div className="topbar-logo" onClick={goHome}>hixrs<span>.</span></div>
            {!accountUser && (
              <button className="mobile-topbar-login" onClick={() => openLoginSheet()}>Sign in</button>
            )}
            {screen !== 'search' && (
              <div className="search-bar-wrap" role="button" tabIndex={0} onClick={() => setScreen('search')} onKeyDown={(e) => { if (e.key === 'Enter') setScreen('search'); }}>
                <span className="search-icon">⌕</span>
                <input
                  className="topbar-search"
                  placeholder="Search Hixs, topics or users..."
                  onFocus={() => setScreen('search')}
                  readOnly
                />
                <span className="search-shortcut">/</span>
              </div>
            )}
            <div className="topbar-topic-wrap">
              {topbarTopics.map(t => (
                <button key={t.key} type="button" className={`topbar-topic-pill ${feedTab === t.key ? 'active' : ''}`} onClick={() => selectTopic(t.key)}>
                  {t.label}
                </button>
              ))}
              <div className="topic-more-wrap">
                <button type="button" className={`topbar-topic-pill ${topbarMoreActive ? 'active' : ''}`} onClick={() => setShowTopbarMore(prev => !prev)}>
                  More
                </button>
                {showTopbarMore && (
                  <div className="topic-more-menu topbar-topic-menu">
                    {topbarMoreTopics.map(t => (
                      <button key={t.key} type="button" className={feedTab === t.key ? 'active' : ''} onClick={() => selectTopic(t.key)}>{t.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="topbar-right">
              {accountUser ? (
                <>
                  <NotificationCenter
                    notifications={notifications}
                    unreadCount={unreadNotificationCount}
                    open={showNotifications}
                    onToggle={() => setShowNotifications(prev => !prev)}
                    onClose={() => setShowNotifications(false)}
                    onOpenPost={openPostWithHistory}
                    onRead={handleNotificationRead}
                    onMarkAllRead={handleMarkAllNotificationsRead} />
                  <div className="topbar-user" onClick={() => { setSelectedProfile(null); setScreen('profile'); }}>
                  <div className="topbar-av">
                    {viewerPhoto ? <img src={viewerPhoto} alt="av" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : viewerInitial}
                  </div>
                </div>
                </>
              ) : (
                <div className="topbar-auth">
                  <button className="topbar-signup" onClick={() => openLoginSheet()}>Sign in</button>
                </div>
              )}
            </div>
          </div>
          {visibleDataError && (
            <div className="data-error-banner">
              <span>{visibleDataError}</span>
              <button type="button" onClick={() => window.location.reload()}>Refresh</button>
            </div>
          )}

          <div className="content-area">
            <div className="feed-area">
              {screen === 'feed' && (
                <div className="screen-wrap">
                  <div className="feed-hero">
                    <div>
                      <div className="hero-kicker">No lock-in, no voice</div>
                      <div className="hero-title">Lock in before the discussion opens.</div>
                    </div>
                    <div className="hero-stats">
                      <span>{posts.length}<small>Hixs</small></span>
                      <span>{posts.reduce((sum, p) => sum + (p.totalVotes || 0), 0)}<small>Lock-ins</small></span>
                    </div>
                  </div>
                  <div className="feed-tabs">
                    {primaryFeedTopics.map(t => (
                      <button key={t}
                        className={`tab ${feedTab === t ? 'active' : ''}`}
                        onClick={() => selectTopic(t)}>
                        {topicLabels[t]}
                      </button>
                    ))}
                    {selectedMoreTopic && (
                      <button className="tab active selected-topic-tab" onClick={() => setShowFeedMore(true)}>{selectedMoreTopic.label}</button>
                    )}
                    <button type="button" className={`tab more-tab ${showFeedMore ? 'active' : ''}`} onClick={() => setShowFeedMore(prev => !prev)} aria-expanded={showFeedMore}>
                      More
                    </button>
                  </div>
                  {showFeedMore && (
                    <div className="feed-more-panel">
                      <div className="feed-more-panel-head">
                        <span>More topics</span>
                        <button type="button" onClick={() => setShowFeedMore(false)}>Close</button>
                      </div>
                      <div className="feed-more-grid">
                        {moreFeedTopics.map(t => (
                          <button key={t.key} type="button" className={feedTab === t.key ? 'active' : ''} onClick={() => selectTopic(t.key)}>
                            <span>{t.label}</span>
                            <small>{posts.filter(post => postMatchesTopic(post, t.key)).length} Hixs</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="feed">
                    {dataErrors.posts
                      ? <EmptyState
                          eyebrow="Connection"
                          title="Could not load Hixs"
                          body={dataErrors.posts}
                          actionLabel="Refresh"
                          onAction={() => window.location.reload()} />
                      : postsLoading
                      ? [1,2,3].map(i => <SkeletonCard key={i} />)
                      : filteredPosts.length === 0
                        ? <EmptyState
                            eyebrow={feedTab === 'all' ? 'Hixs' : 'Topic'}
                            title={feedTab === 'all' ? 'No Hixs yet' : feedTab === 'thoughts' ? 'No Open Thoughts yet' : feedTab === 'hixs' ? 'No Hixs yet' : `No Hixs in ${topicLabels[feedTab] || 'this topic'} yet`}
                            body={feedTab === 'all'
                              ? 'Start the first lock-in and give people something to choose.'
                              : feedTab === 'thoughts'
                                ? 'Share the first discussion-first thought.'
                              : 'This topic is open. Create the first Hix for it.'}
                            actionLabel="Create"
                            onAction={() => setScreen('create')} />
                        : filteredPosts.map(post => (
                          <PostCard key={post.id} post={post}
                            userVote={votes[post.id]}
                            onVote={handleVote}
                            onOpen={openPostWithHistory}
                            requireLogin={requireLogin}
                            user={accountUser}
                            profile={profile}
                            onHeat={handleHeat}
                            onReport={setReportTarget}
                            onPostDelete={requestPostDelete}
                            onPostEdit={requestPostEdit}
                            onProfileOpen={handleProfileOpen}
                            users={users}
                            isLocking={Boolean(pendingLockIns[post.id])} />
                        ))}
                  </div>
                </div>
              )}
              {screen === 'hot' && <HotScreen posts={posts} onOpen={openPostWithHistory} />}
              {screen === 'create' && <CreateScreen onPost={handleCreatePost} requireLogin={requireLogin} user={accountUser} defaultPostType={defaultPostType} />}
              {screen === 'search' && <SearchScreen posts={posts} users={users} postsLoading={postsLoading} onOpen={openPostWithHistory} onProfileOpen={handleProfileOpen} onTopicSelect={(topic) => { if (topic === 'trending') { setScreen('hot'); } else { selectTopic(topic); } }} />}
              {screen === 'publicProfile' && selectedProfile && (
                <PublicProfileScreen
                  target={selectedProfile}
                  users={users}
                  posts={posts}
                  currentUser={accountUser}
                  profile={profile}
                  onBack={() => { setSelectedProfile(null); setScreen('feed'); }}
                  onFollowToggle={handleFollowToggle}
                  requireLogin={requireLogin}
                  onOpenPost={openPostWithHistory} />
              )}
              {screen === 'profile' && (
                <ProfileScreen user={accountUser} profile={profile} votes={votes} posts={posts} users={users}
                  onSignOut={handleSignOut} requireLogin={requireLogin} onSaveProfile={handleSaveProfile} onOpenSettings={() => setScreen('settings')}
                  onPostDelete={requestPostDelete} onPostEdit={requestPostEdit} onOpenPost={openPostWithHistory} onCreatePost={() => setScreen('create')} />
              )}
              {screen === 'settings' && (
                <SettingsScreen user={accountUser} profile={profile}
                  onSaveProfile={handleSaveProfile}
                  onSaveSettings={handleSaveSettings}
                  onSignOut={handleSignOut}
                  onBack={() => setScreen('profile')}
                  requireLogin={requireLogin} />
              )}
            </div>
            {screen !== 'publicProfile' && <RightPanel posts={posts} onOpen={openPostWithHistory} onExploreTopics={() => setScreen('search')} />}
          </div>
        </main>
      </div>

      <nav className="bottom-nav">
        {navItems.map(item => (
          <div key={item.id}
            className={`bnav-item bnav-${item.id} ${screen === item.id || (item.id === 'profile' && (screen === 'settings' || screen === 'publicProfile')) ? 'active' : ''}`}
            onClick={() => setScreen(item.id)}>
            <MobileNavIcon id={item.id} />
            {item.id !== 'create' && <span className="bnav-label">{item.label}</span>}
          </div>
        ))}
      </nav>

      {activePost && (
        <PostModal post={activePost} onClose={closeOpenPost}
          userVote={votes[activePost.id]}
          onVote={handleVote}
          onComment={handleComment}
          onReply={handleReply}
          onCommentLike={handleCommentLike}
          onCommentDelete={handleCommentDelete}
          onPostDelete={requestPostDelete}
          onPostEdit={requestPostEdit}
          onFlip={handleFlip}
          hasFlipped={flippedVotes[activePost.id]}
          onConfirmRequest={requestConfirm}
          onReport={setReportTarget}
          onProfileOpen={handleProfileOpen}
          requireLogin={requireLogin}
          user={accountUser}
          profile={profile} />
      )}

      {showLogin && (
        <LoginSheet
          onClose={(result = {}) => {
            setShowLogin(false);
            setAuthPrompt(null);
            if (!result.signedIn) setPendingAction(null);
          }}
          title={authPrompt?.title}
          subtitle={authPrompt?.subtitle}
          cta={authPrompt?.cta}
        />
      )}

      {editingPost && (
        <EditHixModal
          post={posts.find(p => p.id === editingPost.id) || editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handlePostEdit} />
      )}

      {confirmDialog && (
        <ConfirmModal
          dialog={confirmDialog}
          onClose={() => setConfirmDialog(null)} />
      )}
      {reportTarget && (
        <ReportModal
          target={reportTarget}
          onClose={() => setReportTarget(null)}
          onSubmit={handleReportSubmit} />
      )}

      <Toast message={toast} />
    </div>
  );
}

function App() {
  const [routeKey, setRouteKey] = useState(() => `${window.location.pathname}${window.location.search}${window.location.hash}`);
  const [landingAuthLoading, setLandingAuthLoading] = useState(!firebaseConfigError);
  const [landingUser, setLandingUser] = useState(null);

  useEffect(() => {
    const syncRoute = () => {
      setRouteKey(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    };

    window.addEventListener('hashchange', syncRoute);
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.removeEventListener('hashchange', syncRoute);
      window.removeEventListener('popstate', syncRoute);
    };
  }, []);

  useEffect(() => {
    if (firebaseConfigError) {
      setLandingAuthLoading(false);
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, u => {
      setLandingUser(u);
      setLandingAuthLoading(false);
    }, error => {
      console.error(error);
      setLandingAuthLoading(false);
    });

    return unsub;
  }, []);

  const appRequested =
    routeKey.startsWith('/app') ||
    routeKey.includes('#/app') ||
    routeKey.includes('?app');

  if (!appRequested && landingAuthLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F8FAFC' }}>
        <div style={{ color: '#64748B', fontSize: 13 }}>Loading Hixrs...</div>
      </div>
    );
  }

  if (!appRequested && !landingUser) return <LandingPage />;

  if (firebaseConfigError) {
    return <FirebaseConfigMissing />;
  }

  return <HixrsApp />;
}

export default App;


























